import { useServerList } from "hooks"; // Hook to get active server list
import { useEffect, useState } from "react";
import { getPrefixKey } from "utils"; // Utility to get prefix key
import { checkSubscriberPubsubHealth } from "../services/redisService";

class NetworkStatus {
  constructor() {
    this.isOnline = navigator.onLine;
    this.serverStatuses = {};
    this.subscribers = [];
    this.activeServerList = [];
    this.startMonitoring();
  }

  startMonitoring() {
    // Monitor global network status
    setInterval(() => {
      const currentStatus = navigator.onLine;
      if (this.isOnline !== currentStatus) {
        this.isOnline = currentStatus;
        this.notifySubscribers();
      }
    }, 10000);

    // Monitor server statuses
    setInterval(() => {
      if (this.activeServerList.length > 0) {
        this.checkServerStatuses();
      }
    }, 10000);
  }

  setActiveServerList(serverList) {
    this.activeServerList = serverList;
    this.checkServerStatuses();
  }

  checkServerStatuses() {
    this.activeServerList.forEach(async server => {
      const response = await checkSubscriberPubsubHealth(server.accountId, server.id);
      if (response) {
        this.serverStatuses[server.id] = true;
        this.notifySubscribers();
      }
    });
  }

  checkServerStatusByPrefixKey(prefixKey, isServerOnline) {
    const server = this.activeServerList.find(server => getPrefixKey(server.accountId, server.id) === prefixKey);
    if (!server) return;
    this.serverStatuses[server.id] = isServerOnline;
    this.notifySubscribers();
  }

  subscribe(callback) {
    if (typeof callback === "function") {
      this.subscribers.push(callback);
    }
  }

  unsubscribe(callback) {
    this.subscribers = this.subscribers.filter(subscriber => subscriber !== callback);
  }

  notifySubscribers() {
    this.subscribers.forEach(callback => callback({ isOnline: this.isOnline, serverStatuses: this.serverStatuses }));
  }
}

const networkStatusInstance = new NetworkStatus();

const useNetworkStatus = () => {
  const { activeServerList } = useServerList();
  const [state, setState] = useState({
    isOnline: networkStatusInstance.isOnline,
    serverStatuses: networkStatusInstance.serverStatuses,
  });

  useEffect(() => {
    const handleStatusChange = updatedState => {
      setState(updatedState);
    };

    networkStatusInstance.subscribe(handleStatusChange);
    networkStatusInstance.setActiveServerList(activeServerList);

    return () => {
      networkStatusInstance.unsubscribe(handleStatusChange);
    };
  }, [activeServerList]);

  // Biến lưu thời điểm network failed cho từng prefixKey
  const networkFailed = new Map(); // key: prefixKey, value: timestamp (ms)

  const triggerServerStatusCheck = (prefixKey, isServerOnline) => {
    if (isServerOnline) {
      // Nếu online, cập nhật ngay và reset biến
      networkStatusInstance.checkServerStatusByPrefixKey(prefixKey, true);
      networkFailed.delete(prefixKey);
      return;
    }
    // Nếu offline
    const now = Date.now();
    const lastFailed = networkFailed.get(prefixKey);
    if (!lastFailed) {
      // Lần đầu offline, lưu thời gian
      networkFailed.set(prefixKey, now);
      return;
    }
    // Nếu đã offline trước đó, kiểm tra thời gian
    if (now - lastFailed >= 30000) {
      networkStatusInstance.checkServerStatusByPrefixKey(prefixKey, false);
    }
  };

  return { ...state, triggerServerStatusCheck };
};

export default useNetworkStatus;
